import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { Captcha } from "primeng/captcha";
import { LoginComponent } from "../login/login.component";

// New login UI. All behaviour (forms, captcha, sign-in, forgot password)
// is inherited unchanged from LoginComponent; only the template/styles differ.
@Component({
  selector: "app-login-new",
  templateUrl: "./login-new.component.html",
  styleUrls: ["./login-new.component.css"],
})
export class LoginNewComponent extends LoginComponent implements AfterViewInit, OnDestroy {
  @ViewChild("captchaBox", { static: true }) captchaBox: ElementRef<HTMLElement>;
  @ViewChild(Captcha, { static: true }) captcha: Captcha;

  showPassword = false;
  private captchaObserver: MutationObserver;
  private captchaRetry: any;

  // Company facts shown as cards evenly spaced on an arc over the globe
  // (bl, ml, tl on the left; tr, mr, br on the right - see .ln-fact--* in the CSS).
  stats = [
    { icon: "briefcase", value: "40+", label: "Years of Professional Experience", pos: "tl" },
    { icon: "user", value: "10K+", label: "Satisfied Customers", pos: "tr" },
    { icon: "pin", value: "12", label: "Strategic Locations in India", pos: "ml" },
    { icon: "globe", value: "75+", label: "Countries Worldwide", prefix: "Serving", pos: "mr" },
    { icon: "gear", value: "05", label: "Advanced Manufacturing Facilities", pos: "bl" },
    { icon: "people", value: "750+", label: "Competent Work-force", pos: "br" },
  ];

  // Ignore repeat clicks while the login request is in flight.
  onSubmit() {
    if (this.isLoggingIn) { return; }
    super.onSubmit();
  }

  ngAfterViewInit() {
    this.watchCaptcha();
    this.ensureCaptchaRendered();
  }

  ngOnDestroy() {
    if (this.captchaObserver) { this.captchaObserver.disconnect(); }
    clearInterval(this.captchaRetry);
  }

  // PrimeNG's p-captcha retries only once (after 100ms) if Google's script is still half-loaded,
  // then gives up and the captcha never appears. Keep checking for up to ~20s and render it
  // ourselves once grecaptcha.render is available.
  private ensureCaptchaRendered() {
    const box = this.captchaBox && this.captchaBox.nativeElement;
    let tries = 0;
    this.captchaRetry = setInterval(() => {
      const g = (window as any).grecaptcha;
      const rendered = !!(box && box.querySelector("iframe")) || (this.captcha as any)._instance != null;
      if (rendered || ++tries > 60) { clearInterval(this.captchaRetry); return; }
      if (g && typeof g.render === "function") {
        try { this.captcha.init(); } catch (e) { /* already rendered by PrimeNG - ignore */ }
      }
    }, 350);
  }

  // Only give the captcha space once Google has injected its iframe; if the script is blocked
  // or never loads, the row stays collapsed instead of leaving a blank gap.
  private watchCaptcha() {
    const box = this.captchaBox && this.captchaBox.nativeElement;
    if (!box) { return; }
    const ready = () => {
      if (!box.querySelector("iframe")) { return false; }
      box.classList.add("ln-captcha-ready");
      this.fitCaptcha();
      return true;
    };
    if (ready()) { return; }
    this.captchaObserver = new MutationObserver(() => {
      if (ready()) { this.captchaObserver.disconnect(); }
    });
    this.captchaObserver.observe(box, { childList: true, subtree: true });
  }

  // reCAPTCHA renders at a fixed 304px; scale it so it matches the input width.
  // Set as a CSS variable directly so it doesn't go through change detection.
  @HostListener("window:resize")
  fitCaptcha() {
    const box = this.captchaBox && this.captchaBox.nativeElement;
    if (!box || !box.clientWidth) { return; }
    box.style.setProperty("--cap-scale", String(box.clientWidth / 304));
  }
}
